import { Injectable } from '@angular/core';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { switchMap, catchError, tap, map } from 'rxjs/operators';
import { HttpClient, HttpContext, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { CreateMarketplaceOrderDto, OrderMarketplaceReponse, ProviderStockItem } from '../../interfaces/marketplace.interface';
import { MarketplaceTireProductsResponse, TireFilterResponse, TireFiltersDto } from '../interfaces/tires.interface';
import { TireProduct } from 'src/app/core/interfaces/products-types/tire-product.interface';
import { TranslateService } from '@ngx-translate/core';
import { SKIP_DEDUPE } from 'src/app/core/interceptor/inflight-dedupe.interceptor';

@Injectable({
    providedIn: 'root',
})
export class TiresService {

    private readonly backofficeApi: string = environment.cafler_backoffice_back_url;


    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly analytics: AnalyticsService,
        private readonly translate: TranslateService
    ) { }

    // ========================
    // Helpers
    // ========================
    private authOptions$(): Observable<{ headers: HttpHeaders }> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap(token => {
                if (!token) {
                    return throwError(() => new Error('No token available'));
                }
                const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
                return of({ headers });
            })
        );
    }

    private withAuth<T>(fn: (opts: { headers: HttpHeaders }) => Observable<T>): Observable<T> {
        return this.authOptions$().pipe(
            switchMap((opts) => fn(opts))
        );
    }

    private handleError<T>(operation: string) {
        return (err: unknown): Observable<T> => {
            console.error(`[TyresService] ${operation} error:`, err);
            return throwError(() => (err instanceof Error ? err : new Error(String(err))));
        };
    }

    // ========================
    // Marketplace providers (por EAN + cantidad)
    // Endpoint: `${backofficeApi}/marketplace/providers`
    // ========================
    getMarketplaceProviders(ean: string, quantity: number): Observable<ProviderStockItem[]> {
        if (!ean || quantity <= 0) {
            return of([]);
        }

        const endpoint = `${this.backofficeApi}/marketplace/providers`;

        return this.withAuth<ProviderStockItem[]>((opts) => {
            const params = new HttpParams()
                .set('ean', ean)
                .set('quantity', String(quantity));

            return this.http.get<ProviderStockItem[]>(endpoint, { ...opts, params }).pipe(
                catchError(this.handleError<ProviderStockItem[]>('getMarketplaceProviders'))
            );
        });
    }

    // ========================
    // Crear pedido (book order)
    // Endpoint: `${backofficeApi}/marketplace/orders`
    // ========================
    bookOrder(orderData: CreateMarketplaceOrderDto): Observable<OrderMarketplaceReponse> {
        const endpoint = `${this.backofficeApi}/marketplace/orders`;
        const providerPrice = orderData.providerIdentifier?.providerPrice ?? 0;
        const cartValue = providerPrice * orderData.amount;

        return this.withAuth((opts) =>
            this.http.post<OrderMarketplaceReponse>(endpoint, orderData, opts).pipe(
                catchError(this.handleError<OrderMarketplaceReponse>('bookOrder'))
            )
        ).pipe(
            tap((resp: OrderMarketplaceReponse) => {
                this.analytics.track(AnalyticsEvent.MarketplacePurchaseSuccess, {
                    ...this.analytics.globalProps(),
                    order_id: resp.id,
                    items_count: orderData.amount,
                    cart_value: cartValue,
                });
            }),
            catchError((err: HttpErrorResponse) => {
                const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
                const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

                this.analytics.track(AnalyticsEvent.MarketplacePurchaseFail, {
                    ...this.analytics.globalProps(),
                    items_count: orderData.amount,
                    cart_value: cartValue,
                    error_code: errorCode,
                    error_message: errorMessage,
                    http_status: err.status
                });

                return throwError(() => err);
            })
        );
    }

    getTireProductById(productId: number): Observable<TireProduct> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                return this.http.get<TireProduct>(`${this.backofficeApi}/tire-products/${productId}`, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                });
            })
        );
    }

    createTireProduct(product: TireProduct): Observable<number> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                return this.http.post<TireProduct>(`${this.backofficeApi}/tire-products/`, product, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                }).pipe(
                    map((product) => { return product.id })
                );
            })
        );
    }

    private normalizeMarketplaceTyresResponse(response: MarketplaceTireProductsResponse): MarketplaceTireProductsResponse {
        const items = response.items ?? [];
        const continuationToken = response.continuationToken ?? items[0]?.continuationToken;

        return {
            ...response,
            items,
            currentPage: response.currentPage ?? 1,
            pageSize: response.pageSize ?? items.length,
            totalPages: response.totalPages ?? 0,
            totalCount: response.totalCount ?? response.totalItems ?? 0,
            hasPrevious: response.hasPrevious ?? response.hasPreviousPage ?? false,
            hasNext: response.hasNext ?? response.hasNextPage ?? false,
            continuationToken,
        };
    }

    // Añade este método al servicio (reemplaza o complementa el Subject approach)
    getAvailableProductsAsObservable(
        filters?: Partial<TireFiltersDto>,
        page: number = 1,
        size: number = 15,
    ): Observable<MarketplaceTireProductsResponse> {
        let params = this.getFilterParams(filters)
            .set('pageNumber', page.toString())
            .set('pageSize', size.toString());

        const context = new HttpContext().set(SKIP_DEDUPE, true);

        return this.http.get<MarketplaceTireProductsResponse>(
            `${this.backofficeApi}/tire-products/paginated`,
            { params, context }
        ).pipe(
            map((response) => this.normalizeMarketplaceTyresResponse(response)),
            tap((resp) => {
                this.analytics.track(AnalyticsEvent.MarketplaceSearch, {
                    ...this.analytics.globalProps(),
                    query: filters?.searchText ?? '',
                    results_count: resp.items?.length ?? 0,
                    category: this.translate.instant('categories.tyres')
                });
            })
        );
    }

    getTireFilters(filters?: Partial<TireFiltersDto>): Observable<TireFilterResponse> {
        let params = this.getFilterParams(filters, true)
        const context = new HttpContext().set(SKIP_DEDUPE, true);

        return this.http.get<TireFilterResponse>(`${this.backofficeApi}/tire-products/filters`, {
            params,
            context,
        })

    }

    getFilterParams(filters?: Partial<TireFiltersDto>, filtersRequest?: boolean): HttpParams {
        let reqText = ''

        if (filtersRequest) {
            reqText = ""
        }

        let params = new HttpParams();

        if (filters) {
            if (filters.searchText) {
                params = params.set(reqText + 'SearchText', filters.searchText);
            }

            if (filters.subCategoryId) {
                params = params.set(reqText + 'SubCategoryId', filters.subCategoryId);
            }

            if (filters.qualityId) {
                params = params.set(reqText + 'QualityId', filters.qualityId);
            }

            if (filters.brandIds && filters.brandIds.length > 0) {
                filters.brandIds.forEach(brand => {
                    params = params.append(reqText + 'BrandIds', brand);
                });
            }

            if (filters.season !== null && filters.season !== undefined) {
                params = params.set(reqText + 'Season', filters.season.toString());
            }

            if (filters.width) {
                params = params.set(reqText + 'Width', filters.width);
            }

            if (filters.height) {
                params = params.set(reqText + 'Height', filters.height);
            }

            if (filters.radius) {
                params = params.set(reqText + 'Radius', filters.radius);
            }

            if (filters.loadIndex) {
                params = params.set(reqText + 'LoadIndex', filters.loadIndex);
            }

            if (filters.speedIndex) {
                params = params.set(reqText + 'SpeedIndex', filters.speedIndex);
            }

            if (filters.runflat) {
                params = params.set(reqText + 'Runflat', filters.runflat);
            }

            // if (filters.sortBy !== undefined && filters.sortBy !== null) {
            //     params = params.set('sortBy', filters.sortBy.toString());
            // }
            // if (filters.sortOrder !== undefined && filters.sortOrder !== null) {
            //     params = params.set('sortOrder', filters.sortOrder.toString());
            // }
        }

        return params;
    }
}
